/**
 * Structure commune des ecrans : en-tete, cartes et zone de tableau. Un seul
 * gabarit garde le meme rythme d'un ecran a l'autre.
 */
import type { ReactNode, Ref } from 'react';

/** En-tete uniforme : titre a gauche, actions a droite, sous-titre dessous. */
export function EnTetePage({
  titre,
  sousTitre,
  actions,
  fil,
  refTitre,
}: {
  titre: string;
  sousTitre?: ReactNode;
  actions?: ReactNode;
  /** Fil d'Ariane affiche au-dessus du titre. */
  fil?: ReactNode;
  /** Rend le titre focalisable : repli du focus quand le controle qui le tenait disparait. */
  refTitre?: Ref<HTMLHeadingElement>;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {fil}
        <h1 ref={refTitre} tabIndex={refTitre ? -1 : undefined} className="text-titre-page font-semibold tracking-titre text-encre focus:outline-none">
          {titre}
        </h1>
        {sousTitre && <p className="mt-1 text-sm text-encre-discrete">{sousTitre}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Carte({
  titre,
  description,
  action,
  children,
  className = '',
  /** Retire le rembourrage interne : pour un tableau ou une liste pleine largeur. */
  sansMarge = false,
  refTitre,
}: {
  titre?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  sansMarge?: boolean;
  /** Rend le titre focalisable : repli du focus quand le controle qui le tenait disparait. */
  refTitre?: Ref<HTMLHeadingElement>;
}) {
  return (
    <section className={`panneau ${className}`}>
      {(titre || action) && (
        <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-bordure px-4 py-3.5 sm:px-5">
          <div className="min-w-0">
            {titre && (
              <h2 ref={refTitre} tabIndex={refTitre ? -1 : undefined} className="text-md font-semibold tracking-titre text-encre focus:outline-none">
                {titre}
              </h2>
            )}
            {description && <p className="mt-0.5 text-sm text-encre-discrete">{description}</p>}
          </div>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </header>
      )}
      <div className={sansMarge ? '' : 'p-4 sm:p-5'}>{children}</div>
    </section>
  );
}

/**
 * Zone de defilement d'un tableau dense.
 * Elle borne la hauteur pour que l'en-tete collant ait un contexte de
 * defilement, et laisse le debordement horizontal a l'interieur du panneau.
 */
export function ZoneTableau({
  libelle,
  children,
  className = '',
  ref,
}: {
  /** Nom de la region defilante, annonce par les lecteurs d'ecran. */
  libelle: string;
  children: ReactNode;
  className?: string;
  /** Pour y replacer le focus quand le controle qui le tenait disparait. */
  ref?: Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      /* `tabIndex` rend la zone atteignable au clavier : sans lui, un tableau
         plus large que l'ecran ne peut pas etre fait defiler sans souris. */
      tabIndex={0}
      role="region"
      aria-label={libelle}
      className={`max-h-[calc(100vh-17rem)] overflow-auto ${className}`}
    >
      {children}
    </div>
  );
}
